import { Box, Flex, type UseToastOptions } from '@chakra-ui/react'
import { useCallback, useEffect } from 'react'

import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import isUserAgeAssured from '@/client/services/ageAssurance/isUserAgeAssured'
import { hasUserRole } from '@/client/services/user'

import AgeAssuranceFailureNotice from '@/components/AgeAssurance/AgeAssuranceFailureNotice'
import VerifyAgeButton from '@/components/Profile/AgeDetail/VerifyAgeButton'
import { useToaster } from '@/components/Toast/useToaster'

const AGE_ASSURANCE_TOAST_KEY = 'hasPresentedAgeAssuranceToast'

const failedToastOptions: Partial<UseToastOptions> = {
  status: 'error',
  id: 'age-assurance-failed',
  description: <AgeAssuranceFailureNotice />,
}

function NeedToAssureToastDescription() {
  return (
    <Flex direction="row">
      <Box>You must verify your age before you can join your upcoming session.</Box>
      <Flex justifyContent="center" alignItems="center" mx="8px">
        <VerifyAgeButton
          bgColor="warning.solid"
          _hover={{ bgColor: 'warning.fg', cursor: 'pointer' }}
        />
      </Flex>
    </Flex>
  )
}

export default function AgeAssuranceToast() {
  const user = useUser()
  const toast = useToaster()
  const query = useQuery()

  const me = query.one(UserQ.where({ id: user.id }))
  const hasUpcomingRegisteredSession =
    me<boolean>(
      SQL`EXISTS (SELECT 1 ${SessionQ.where(
        {},
        SQL`
          ${SessionQ.hasUserRegisteredForSeries(UserQ.id)}
          AND ${SessionQ.getComputedEndDate()} > NOW()
        `
      )})`
    ) ?? false

  const hasFailedAgeAssurance = hasUserRole(user, 'user:age_assurance:failed')
  const shouldPresentToast =
    (hasUpcomingRegisteredSession && !isUserAgeAssured(user)) || hasFailedAgeAssurance

  const presentToast = useCallback(() => {
    if (sessionStorage.getItem(AGE_ASSURANCE_TOAST_KEY) != null) {
      return
    }
    sessionStorage.setItem(AGE_ASSURANCE_TOAST_KEY, 'true')

    const needToAssureToastOptions: Partial<UseToastOptions> = {
      status: 'warning',
      id: 'age-assurance-required',
      description: <NeedToAssureToastDescription />,
    }

    toast({
      duration: null,
      ...(hasFailedAgeAssurance ? failedToastOptions : needToAssureToastOptions),
    })
  }, [toast, hasFailedAgeAssurance])

  useEffect(() => {
    if (shouldPresentToast) {
      // We add a delay so the toast container has time to mount
      setTimeout(presentToast, 1000)
    }
  }, [shouldPresentToast])

  return null
}
